'use client';

import type { InternalNetworkAssetDetailDto } from '@terra/shared-contracts-internal-network';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  LabelDefault,
  MetaDefault,
  toasterApi,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  useDismissCanonicalInternalNetworkResolutionIssue,
  useKeepCanonicalInternalNetworkAssetsSeparate,
  useMergeCanonicalInternalNetworkAsset,
  useReverseCanonicalInternalNetworkAssetMerge,
} from '../../../hooks/api/useCanonicalInternalNetworkAssetMerge';

export function CanonicalInternalNetworkAssetMergeActions({
  networkId,
  asset,
}: {
  networkId: string;
  asset: InternalNetworkAssetDetailDto;
}) {
  const tCommon = useTranslations('common');
  const tList = useTranslations('internalNetwork.assetList');
  const mergeMutation = useMergeCanonicalInternalNetworkAsset({ networkId });
  const reverseMutation = useReverseCanonicalInternalNetworkAssetMerge({
    networkId,
  });
  const dismissMutation = useDismissCanonicalInternalNetworkResolutionIssue({
    networkId,
  });
  const keepSeparateMutation = useKeepCanonicalInternalNetworkAssetsSeparate({
    networkId,
  });

  const openIssues = asset.resolutionIssues.filter(
    (issue) => issue.status === 'OPEN',
  );
  const reversibleMergeEventId = asset.latestReversibleMergeEventId;
  const isPending =
    mergeMutation.isPending ||
    reverseMutation.isPending ||
    dismissMutation.isPending ||
    keepSeparateMutation.isPending;

  if (openIssues.length === 0 && !reversibleMergeEventId) {
    return null;
  }

  const toastSuccess = () => {
    toasterApi.success({ title: tCommon('success') });
  };

  const toastError = () => {
    toasterApi.error({ title: tCommon('error') });
  };

  return (
    <VStack
      alignItems="stretch"
      gap={2}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      p={4}
    >
      <LabelDefault>
        {tList(`resolutionState.${asset.resolutionState}`)}
      </LabelDefault>
      {openIssues.map((issue) => (
        <VStack key={issue.id} alignItems="stretch" gap={2}>
          <MetaDefault color="fg.muted">
            {tList(`resolutionIssueKind.${issue.kind}`)} ·{' '}
            {tList(`resolutionIssueStatus.${issue.status}`)}
          </MetaDefault>
          {issue.candidateAssetIds.map((candidateAssetId) => (
            <HStack
              key={`${issue.id}:${candidateAssetId}`}
              justifyContent="space-between"
              gap={3}
            >
              <BodyDefault truncate>{candidateAssetId}</BodyDefault>
              <Button
                size="xs"
                variant="outline"
                disabled={isPending}
                aria-label={tCommon('confirm')}
                onClick={() => {
                  mergeMutation.mutate(
                    {
                      sourceAssetId: asset.id,
                      targetAssetId: candidateAssetId,
                      reason: issue.kind,
                    },
                    {
                      onSuccess: toastSuccess,
                      onError: toastError,
                    },
                  );
                }}
              >
                {tCommon('confirm')}
              </Button>
            </HStack>
          ))}
          <HStack justifyContent="flex-end" gap={2}>
            <Button
              size="xs"
              variant="ghost"
              disabled={isPending}
              aria-label={tCommon('reject')}
              onClick={() => {
                keepSeparateMutation.mutate(
                  {
                    issueId: issue.id,
                    reason: issue.kind,
                  },
                  {
                    onSuccess: toastSuccess,
                    onError: toastError,
                  },
                );
              }}
            >
              {tCommon('reject')}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={isPending}
              aria-label={tCommon('discard')}
              onClick={() => {
                dismissMutation.mutate(
                  {
                    issueId: issue.id,
                    reason: issue.kind,
                  },
                  {
                    onSuccess: toastSuccess,
                    onError: toastError,
                  },
                );
              }}
            >
              {tCommon('discard')}
            </Button>
          </HStack>
        </VStack>
      ))}
      {reversibleMergeEventId ? (
        <HStack justifyContent="flex-end">
          <Button
            size="xs"
            variant="ghost"
            disabled={isPending}
            aria-label={tCommon('discard')}
            onClick={() => {
              reverseMutation.mutate(
                {
                  mergeEventId: reversibleMergeEventId,
                  reason: 'MERGED',
                },
                {
                  onSuccess: toastSuccess,
                  onError: toastError,
                },
              );
            }}
          >
            {tCommon('discard')}
          </Button>
        </HStack>
      ) : null}
    </VStack>
  );
}
